import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { fetchClientDetail } from "../../../services/ClientsService";
import { buildTasteSections } from "../../../utils/profileTastes";
import "./Clients.css";

const getInitials = (name = "") => {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return (parts[0] || "?").slice(0, 2).toUpperCase();
};

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const normalizePhoneDigits = (value = "") =>
  String(value || "")
    .replace(/[^\d+]/g, "")
    .replace(/^\+/, "")
    .trim();

const uniquePhones = (phones = [], fallback = "") => {
  const list = Array.isArray(phones) ? [...phones] : [];
  if (fallback) list.push(fallback);

  const unique = [];
  list.forEach((phone) => {
    const cleaned = String(phone || "").trim();
    if (!cleaned) return;
    const normalized = normalizePhoneDigits(cleaned);
    if (!normalized) return;
    const alreadyListed = unique.some((existing) => {
      const existingDigits = normalizePhoneDigits(existing);
      return (
        existingDigits === normalized ||
        existingDigits.endsWith(normalized) ||
        normalized.endsWith(existingDigits)
      );
    });
    if (!alreadyListed) unique.push(cleaned);
  });
  return unique;
};

const INTERACTIONS_PAGE_SIZE = 3;

const decodeClientParam = (value) => {
  const raw = String(value || "");
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
};

const getClaimTypeLabels = (item, t) => {
  const types = Array.isArray(item.claimTypes) ? item.claimTypes : [];
  if (!types.length) return t("claimCategory_other");
  return types
    .map((type) => {
      if (type === "other" && item.customType) return item.customType;
      return t(`claimCategory_${type}`, type);
    })
    .join(", ");
};

const getInteractionIcon = (type) => {
  if (type === "table") return "fa-utensils";
  if (type === "event") return "fa-calendar-alt";
  return "fa-file-alt";
};

const getInteractionTitle = (item, t) => {
  if (item.type === "table") return t("clientsInteractionTable");
  if (item.type === "event") return t("clientsInteractionEvent");
  return t("clientsInteractionClaim");
};

const ClientDetail = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { clientId: pathClientId } = useParams();
  const [searchParams] = useSearchParams();
  const clientId =
    searchParams.get("id") || decodeClientParam(pathClientId);
  const [loading, setLoading] = useState(true);
  const [client, setClient] = useState(null);
  const [account, setAccount] = useState(null);
  const [interactions, setInteractions] = useState([]);
  const [interactionsPage, setInteractionsPage] = useState(1);

  const loadDetail = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const data = await fetchClientDetail(clientId);
      setClient(data?.client || null);
      setAccount(data?.account || null);
      setInteractions(Array.isArray(data?.interactions) ? data.interactions : []);
      setInteractionsPage(1);
    } catch (error) {
      toast.error(
        error?.response?.data?.message || t("clientsDetailLoadError")
      );
      setClient(null);
      setAccount(null);
      setInteractions([]);
      setInteractionsPage(1);
    } finally {
      setLoading(false);
    }
  }, [clientId, t]);

  useEffect(() => {
    void loadDetail();
  }, [loadDetail]);

  const tasteSections = useMemo(
    () => buildTasteSections(account?.profileExtend),
    [account?.profileExtend]
  );

  const interactionsTotalPages = Math.max(
    1,
    Math.ceil(interactions.length / INTERACTIONS_PAGE_SIZE)
  );
  const currentInteractionsPage = Math.min(
    interactionsPage,
    interactionsTotalPages
  );
  const pageInteractions = interactions.slice(
    (currentInteractionsPage - 1) * INTERACTIONS_PAGE_SIZE,
    currentInteractionsPage * INTERACTIONS_PAGE_SIZE
  );

  return (
    <>
      <div className="clients-page">
        <div className="clients-detail-top">
          <button
            type="button"
            className="clients-back-btn"
            onClick={() => navigate("/clients")}
          >
            <i className="fas fa-arrow-left" aria-hidden="true" />
            {t("clientsBack")}
          </button>
        </div>

        {loading ? (
          <div className="clients-shell">
            <div className="clients-empty">{t("loading")}...</div>
          </div>
        ) : !client ? (
          <div className="clients-shell">
            <div className="clients-empty">
              <strong>{t("clientsNotFound")}</strong>
              <button
                type="button"
                className="clients-view-btn"
                onClick={() => navigate("/clients")}
              >
                {t("clientsBack")}
              </button>
            </div>
          </div>
        ) : (
          <>
            <header className="clients-profile-hero">
              <div className="clients-profile-hero__main">
                <span className="clients-avatar is-xl" aria-hidden="true">
                  {getInitials(client.displayName)}
                </span>
                <div>
                  <span className="clients-hero__eyebrow">
                    {t("clientsConsult")}
                  </span>
                  <h2>{client.displayName}</h2>
                  <p>{t("clientsConsultSubtitle")}</p>
                  <div className="clients-source-badges">
                    {(client.sources || []).map((source) => (
                      <span
                        key={source}
                        className={`clients-source-badge is-${source}`}
                      >
                        {source === "table"
                          ? t("clientsSourceTable")
                          : source === "event"
                            ? t("clientsSourceEvent")
                            : t("clientsSourceClaim")}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              <div className="clients-hero__stats">
                <div className="clients-stat">
                  <i className="fas fa-chart-line" aria-hidden="true" />
                  <div>
                    <strong>{client.visitCount || 0}</strong>
                    <span>{t("clientsVisits")}</span>
                  </div>
                </div>
                <div className="clients-stat">
                  <i className="fas fa-utensils" aria-hidden="true" />
                  <div>
                    <strong>{client.tableReservationCount || 0}</strong>
                    <span>{t("clientsFromTables")}</span>
                  </div>
                </div>
                <div className="clients-stat">
                  <i className="fas fa-calendar-check" aria-hidden="true" />
                  <div>
                    <strong>{client.eventReservationCount || 0}</strong>
                    <span>{t("clientsFromEvents")}</span>
                  </div>
                </div>
                <div className="clients-stat">
                  <i className="fas fa-file-alt" aria-hidden="true" />
                  <div>
                    <strong>{client.claimCount || 0}</strong>
                    <span>{t("clientsFromClaims")}</span>
                  </div>
                </div>
              </div>
            </header>

            <section className="clients-profile-grid">
              <article className="clients-info-card clients-info-card--contact">
                <div className="clients-info-card__head">
                  <h3>{t("clientsContactInfo")}</h3>
                  <span className="clients-info-card__head-badge is-placeholder" aria-hidden="true" />
                </div>

                <div className="clients-info-card__body">
                  <div className="clients-contact-list">
                    <div className="clients-contact-item">
                      <span className="clients-contact-item__icon">
                        <i className="fas fa-phone" aria-hidden="true" />
                      </span>
                      <div>
                        <small>{t("clientsPhone")}</small>
                        <strong>
                          {uniquePhones(
                            client.phones,
                            client.phone || client.whatsapp
                          ).join(", ") || "—"}
                        </strong>
                      </div>
                    </div>
                    <div className="clients-contact-item">
                      <span className="clients-contact-item__icon">
                        <i className="fas fa-envelope" aria-hidden="true" />
                      </span>
                      <div>
                        <small>{t("clientsEmail")}</small>
                        <strong>
                          {(client.emails || []).join(", ") ||
                            client.email ||
                            "—"}
                        </strong>
                      </div>
                    </div>
                    <div className="clients-contact-item">
                      <span className="clients-contact-item__icon">
                        <i className="fab fa-whatsapp" aria-hidden="true" />
                      </span>
                      <div>
                        <small>{t("clientsWhatsapp")}</small>
                        <strong>{client.whatsapp || "—"}</strong>
                      </div>
                    </div>
                    <div className="clients-contact-item">
                      <span className="clients-contact-item__icon">
                        <i className="fas fa-comment-dots" aria-hidden="true" />
                      </span>
                      <div>
                        <small>{t("clientsPreferred")}</small>
                        <strong>{client.preferredContact || "—"}</strong>
                      </div>
                    </div>
                    <div className="clients-contact-item">
                      <span className="clients-contact-item__icon">
                        <i className="fas fa-globe" aria-hidden="true" />
                      </span>
                      <div>
                        <small>{t("clientsCountry")}</small>
                        <strong>{client.country || "—"}</strong>
                      </div>
                    </div>
                    {account && (
                      <div className="clients-contact-item">
                        <span className="clients-contact-item__icon">
                          <i className="fas fa-user-check" aria-hidden="true" />
                        </span>
                        <div>
                          <small>{t("clientsAppAccount") || "App account"}</small>
                          <strong>
                            {account.displayName}
                            {account.provider ? ` · ${account.provider}` : ""}
                          </strong>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="clients-date-strip">
                    <div>
                      <small>{t("clientsFirstSeen")}</small>
                      <strong>{formatDate(client.firstSeenAt)}</strong>
                    </div>
                    <div>
                      <small>{t("clientsLastSeen")}</small>
                      <strong>{formatDate(client.lastSeenAt)}</strong>
                    </div>
                  </div>
                </div>
              </article>

              {(account || tasteSections.length > 0) && (
                <article className="clients-info-card">
                  <div className="clients-info-card__head">
                    <h3>{t("clientsDiningTastes") || "Dining tastes"}</h3>
                  </div>
                  <div className="clients-info-card__body">
                    {tasteSections.length === 0 ? (
                      <div className="clients-empty is-compact">
                        <strong>
                          {t("clientsNoTastes") ||
                            "This guest has not shared tastes yet."}
                        </strong>
                      </div>
                    ) : (
                      <div className="clients-taste-grid">
                        {tasteSections.map((section) => (
                          <article key={section.key} className="clients-taste-card">
                            <header>
                              <strong>{section.title}</strong>
                              <span>{section.hint}</span>
                            </header>
                            <ul>
                              {section.rows.map((row) => (
                                <li key={row.label}>
                                  <small>{row.label}</small>
                                  <div className="clients-taste-chips">
                                    {row.chips.map((chip) => (
                                      <em key={chip}>{chip}</em>
                                    ))}
                                  </div>
                                </li>
                              ))}
                            </ul>
                          </article>
                        ))}
                      </div>
                    )}
                  </div>
                </article>
              )}

              <article className="clients-info-card clients-info-card--interactions">
                <div className="clients-info-card__head">
                  <h3>{t("clientsInteractions")}</h3>
                  <span className="clients-info-card__head-badge">{interactions.length}</span>
                </div>

                <div className="clients-info-card__body">
                  {interactions.length === 0 ? (
                    <div className="clients-empty is-compact">
                      <strong>{t("clientsNoInteractions")}</strong>
                    </div>
                  ) : (
                    <>
                      <ul className="clients-timeline">
                        {pageInteractions.map((item) => (
                          <li key={`${item.type}-${item.id}`}>
                            <span
                              className={`clients-timeline__rail is-${item.type}`}
                              aria-hidden="true"
                            >
                              <i
                                className={`fas ${getInteractionIcon(item.type)}`}
                              />
                            </span>
                            <div className="clients-timeline__body">
                              <div className="clients-timeline__head">
                                <strong>{getInteractionTitle(item, t)}</strong>
                                <em
                                  className={`clients-status is-${item.status}`}
                                >
                                  {item.status || "—"}
                                </em>
                              </div>
                              <p>
                                {item.type === "claim"
                                  ? getClaimTypeLabels(item, t)
                                  : item.type === "event" && item.eventTitle
                                    ? item.eventTitle
                                    : `${t("clientsPartySize")}: ${
                                        item.partySize || "—"
                                      }${
                                        item.type === "table" && item.tableNumber
                                          ? ` · ${t("reservationTable")} ${
                                              item.tableNumber
                                            }`
                                          : ""
                                      }`}
                              </p>
                              {item.type === "claim" && item.description ? (
                                <p className="clients-timeline__excerpt">
                                  {item.description}
                                </p>
                              ) : null}
                              <small>
                                {item.type === "table"
                                  ? formatDateTime(item.startAt || item.at)
                                  : item.type === "claim"
                                    ? `${formatDateTime(item.at)}${
                                        item.visitDateTime
                                          ? ` · ${t("clientsClaimVisitDate")}: ${item.visitDateTime}`
                                          : ""
                                      }`
                                    : `${formatDate(
                                        item.reservationDate || item.at
                                      )}${
                                        item.reservationTime
                                          ? ` · ${item.reservationTime}`
                                          : ""
                                      }`}
                              </small>
                              <div className="clients-timeline__actions">
                                {item.type === "event" && item.eventId ? (
                                  <Link
                                    className="clients-timeline__link"
                                    to={`/events/${item.eventId}/reservations`}
                                  >
                                    {t("clientsOpenEventReservations")}
                                  </Link>
                                ) : null}
                                {item.type === "table" ? (
                                  <Link
                                    className="clients-timeline__link"
                                    to="/reservations"
                                  >
                                    {t("clientsOpenReservations")}
                                  </Link>
                                ) : null}
                                {item.type === "claim" ? (
                                  <Link
                                    className="clients-timeline__link"
                                    to="/client-reclamations"
                                  >
                                    {t("clientsOpenClaims")}
                                  </Link>
                                ) : null}
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>

                      {interactions.length > INTERACTIONS_PAGE_SIZE ? (
                        <div className="clients-interactions-pagination">
                          <button
                            type="button"
                            disabled={currentInteractionsPage <= 1}
                            onClick={() =>
                              setInteractionsPage((prev) =>
                                Math.max(1, prev - 1)
                              )
                            }
                          >
                            {t("previous")}
                          </button>
                          <span>
                            {currentInteractionsPage} / {interactionsTotalPages}
                          </span>
                          <button
                            type="button"
                            disabled={
                              currentInteractionsPage >= interactionsTotalPages
                            }
                            onClick={() =>
                              setInteractionsPage((prev) =>
                                Math.min(interactionsTotalPages, prev + 1)
                              )
                            }
                          >
                            {t("next")}
                          </button>
                        </div>
                      ) : null}
                    </>
                  )}
                </div>
              </article>
            </section>
          </>
        )}
      </div>
    </>
  );
};

export default ClientDetail;
